import React, { useState, useEffect } from 'react';
import './ProjectsDashboard.css';
import BulkUpload from './BulkUpload';
import Topbar from '../../component/Topbar';
import SideMenu from '../../component/SideMenu';
import ExportProject from './ExportProject';
import CreateFolder from './CreateFolder';
import AdvancedFilterModal from './AdvancedFilterModal'; 
import { baseurl } from '../../baseurl';
import axios from 'axios';
import Swal from 'sweetalert2';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Link, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';

const ProjectsDashboard = () => {

  const location = useLocation();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [reportFilter, setReportFilter] = useState('all');
  const [selectedProjects, setSelectedProjects] = useState([]);


  const [statusModalProject, setStatusModalProject] = useState(null);
const [updatingStatus, setUpdatingStatus] = useState(false);



  // --- Advanced Filter States ---
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [advFilters, setAdvFilters] = useState({
    dateRange: 'any',
    lotSize: 'any',
    sortBy: 'newest',
    folder: 'any' // This will store the folder_id or 'any'
  });


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const folderId = params.get('i');
    const folderName = params.get('n');

    if (folderId) {
      setAdvFilters(prev => ({
        ...prev,
        folder: folderId // This will set the advanced filter automatically
      }));
      console.log(`Filtering by Folder: ${folderName} (ID: ${folderId})`);
    }
  }, [location]);



  const handleStatusChange = async () => {
  if (!statusModalProject) return;
  const userToken = localStorage.getItem('userToken');
  setUpdatingStatus(true);
  try {
    await axios.post(`${baseurl}updateProjectStatus`, 
      { projectId: statusModalProject.id, status: 'Pending' },
      { headers: { Authorization: `Bearer ${userToken}` } }
    );
    setStatusModalProject(null);
    fetchProjects();
  } catch (err) {
    Swal.fire('Error!', 'Failed to update status.', 'error');
  } finally {
    setUpdatingStatus(false);
  }
};





  const fetchProjects = async () => {
    const userToken = localStorage.getItem('userToken');
    try {
      setLoading(true);
      const response = await axios.get(`${baseurl}getUserProjects`, {
        headers: { Authorization: `Bearer ${userToken}` }
      });

      console.log(response.data);
      if (response.data.success) {
        setProjects(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Handler for applying filters from the child modal
  const handleApplyFilters = (newFilters) => {
    console.log("[Dashboard] Received New Filters:", newFilters);
    setAdvFilters(newFilters);
    setShowAdvancedModal(false);
  };

  const handleResetFilters = (resetValues) => {
    console.log("[Dashboard] Filters Reset to default.");
    setAdvFilters(resetValues);
  };

  const handleDelete = async (ids) => {
    const userToken = localStorage.getItem('userToken');
    const projectIds = Array.isArray(ids) ? ids : [ids];

    if (projectIds.length === 0) {
      Swal.fire({ icon: 'info', title: 'No Selection', text: 'Please select at least one project to delete.' });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${projectIds.length} project(s). This step is non-reversible and it will NOT reverse credits to your account.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(`${baseurl}deleteProjects`, 
          { projectIds: projectIds }, 
          { headers: { Authorization: `Bearer ${userToken}` } }
        );
        if (response.data.success) {
          Swal.fire('Deleted!', 'Your projects have been deleted.', 'success');
          setSelectedProjects([]);
          fetchProjects();
        }
      } catch (err) {
        Swal.fire('Error!', 'An error occurred while deleting projects.', 'error');
      }
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedProjects(filteredProjects.map(p => p.id));
    } else {
      setSelectedProjects([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedProjects(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

const handleDownloadExcel = () => {
  if (selectedProjects.length === 0) {
    Swal.fire({ icon: 'info', title: 'No Selection', text: 'Please select at least one project to download.' });
    return;
  }

  const dataToExport = projects
    .filter(p => selectedProjects.includes(p.id))
    .map((p, index) => {
      const rawArea = p.project_data?.lotArea || '';
      const areaNum = parseFloat(rawArea.toString().replace(/[^\d.-]/g, '')) || 0;
      const isAcre  = rawArea.toString().toLowerCase().includes('acre');
      const sqft    = isAcre ? (areaNum * 43560) : areaNum;
      const acres   = (sqft / 43560).toFixed(4);
      const lotAreaFormatted = areaNum > 0
        ? `${sqft.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Sqft / ${acres} Acres`
        : '-';

      return {
        "Sr. No":          index + 1,
        "Property ID":     p.property_id || '-',
        "Status":          p.status || '-',
        "Address":         p.project_data?.address || '-',
        "Lot Area":        lotAreaFormatted,
        "Created Date":    p.created_at  ? new Date(p.created_at).toLocaleString('en-US',  { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'UTC', hour12: false }) : '-',
        "Completed Date":  p.complete_at ? new Date(p.complete_at).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'UTC', hour12: false }) : 'N/A',
        "Report Type":     p.project_data?.reportType || '-',
      };
    });

  const worksheet = XLSX.utils.json_to_sheet(dataToExport, { skipHeader: false });
  const headerKeys = Object.keys(dataToExport[0] || {});

  // ── Header style (dark blue bg, white bold text, centered) ──
  const headerStyle = {
    font:      { bold: true, color: { rgb: 'FFFFFF' }, sz: 11, name: 'Calibri' },
    fill:      { fgColor: { rgb: '1F4E79' }, patternType: 'solid' },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: false },
    border: {
      top:    { style: 'thin', color: { rgb: '2E75B6' } },
      bottom: { style: 'thin', color: { rgb: '2E75B6' } },
      left:   { style: 'thin', color: { rgb: '2E75B6' } },
      right:  { style: 'thin', color: { rgb: '2E75B6' } },
    }
  };

  // ── Even row (light blue) ──
  const evenRowStyle = {
    font:      { sz: 10, name: 'Calibri', color: { rgb: '1A1A1A' } },
    fill:      { fgColor: { rgb: 'D6E4F0' }, patternType: 'solid' },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: false },
    border: {
      top:    { style: 'thin', color: { rgb: 'BDD7EE' } },
      bottom: { style: 'thin', color: { rgb: 'BDD7EE' } },
      left:   { style: 'thin', color: { rgb: 'BDD7EE' } },
      right:  { style: 'thin', color: { rgb: 'BDD7EE' } },
    }
  };

  // ── Odd row (white) ──
  const oddRowStyle = {
    font:      { sz: 10, name: 'Calibri', color: { rgb: '1A1A1A' } },
    fill:      { fgColor: { rgb: 'FFFFFF' }, patternType: 'solid' },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: false },
    border: {
      top:    { style: 'thin', color: { rgb: 'BDD7EE' } },
      bottom: { style: 'thin', color: { rgb: 'BDD7EE' } },
      left:   { style: 'thin', color: { rgb: 'BDD7EE' } },
      right:  { style: 'thin', color: { rgb: 'BDD7EE' } },
    }
  };

  // ── Sr. No column — center aligned ──
  const srNoStyle = (base) => ({
    ...base,
    alignment: { horizontal: 'center', vertical: 'center', wrapText: false }
  });

  // Apply styles
  Object.keys(worksheet).forEach(cellRef => {
    if (cellRef.startsWith('!')) return;
    const match = cellRef.match(/^([A-Z]+)(\d+)$/);
    if (!match) return;
    const col    = match[1];
    const rowNum = parseInt(match[2], 10);
    const colIdx = XLSX.utils.decode_col(col);

    if (!worksheet[cellRef].s) worksheet[cellRef].s = {};

    if (rowNum === 1) {
      worksheet[cellRef].s = headerStyle;
    } else {
      const base = rowNum % 2 === 0 ? evenRowStyle : oddRowStyle;
      // Sr. No column (index 0) gets center alignment
      worksheet[cellRef].s = colIdx === 0 ? srNoStyle(base) : base;
    }
  });

  // ── Column widths ──
  worksheet['!cols'] = headerKeys.map(key => {
    const maxLen = Math.max(
      key.length,
      ...dataToExport.map(row => (row[key] != null ? row[key].toString().length : 0))
    );
    return { wch: maxLen + 5 };
  });

  // ── Row heights ──
  worksheet['!rows'] = [
    { hpt: 20 },
    ...dataToExport.map(() => ({ hpt: 17 }))
  ];

  // ── Freeze header row ──
  worksheet['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft' };

  // ── NO autofilter — remove it entirely ──
  delete worksheet['!autofilter'];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");
  XLSX.writeFile(workbook, `Projects_Export_${new Date().getTime()}.xlsx`, { cellStyles: true });
};

  // --- GET DISTINCT FOLDERS FROM PROJECTS DATA ---
  const distinctFolders = Array.from(
    new Map(
      projects
        .filter(p => p.folder_id) // Only projects with a folder
        .map(p => [p.folder_id, { id: p.folder_id, name: p.folder_name }])
    ).values()
  );

  // --- Filtering Logic ---
  const filteredProjects = projects.filter(proj => {
    const status = proj.status.toLowerCase();
    const matchesTab = activeTab === 'all' || 
                       (activeTab === 'draft' && status === 'draft') ||
                       (activeTab === 'queued' && status === 'pending');
    
    const matchesReport = reportFilter === 'all' || proj.project_data?.reportType === reportFilter;
    const matchesSearch = (proj.project_data?.address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (proj.property_id || '').toLowerCase().includes(searchTerm.toLowerCase());

    // Lot Size Filter
    let matchesLotSize = true;
    const areaStr = proj.project_data?.lotArea || "";
    const areaNum = parseFloat(areaStr.toString().replace(/[^\d.-]/g, '')) || 0;
    const isAcre = areaStr.toString().toLowerCase().includes('acre');
    const areaInAcres = isAcre ? areaNum : areaNum / 43560;

    if (advFilters.lotSize === 'small') matchesLotSize = areaInAcres < 0.5;
    else if (advFilters.lotSize === 'medium') matchesLotSize = areaInAcres >= 0.5 && areaInAcres <= 2;
    else if (advFilters.lotSize === 'large') matchesLotSize = areaInAcres > 2;

    // Folder Filter
    let matchesFolder = true;
    if (advFilters.folder !== 'any') {
        matchesFolder = proj.folder_id === parseInt(advFilters.folder);
    }

    return matchesTab && matchesReport && matchesSearch && matchesLotSize && matchesFolder;
  }).sort((a, b) => {
    if (advFilters.sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
    if (advFilters.sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
    return 0;
  });

  const distinctReportTypes = [...new Set(projects.map(p => p.project_data?.reportType).filter(Boolean))];
  const draftCount = projects.filter(p => p.status.toLowerCase() === 'draft').length;
  const queuedCount = projects.filter(p => p.status.toLowerCase() === 'pending').length;

  const formatLotArea = (areaValue) => {
    if (!areaValue) return '-';
    const num = parseFloat(areaValue.toString().replace(/[^\d.-]/g, ''));
    if (isNaN(num)) return areaValue;
    const ACRE_TO_SQFT = 43560;
    const isAcre = areaValue.toString().toLowerCase().includes('acre');

    if (isAcre) {
      return (<>{(num * ACRE_TO_SQFT).toLocaleString()} sqft /<br />{num} acre</>);
    } else {
      return (<>{num.toLocaleString()} sqft /<br />{(num / ACRE_TO_SQFT).toFixed(3)} acre</>);
    }
  };







  const handleShare = async (propertyId) => {
  const shareUrl = `${window.location.origin}/report/${propertyId}`; // Adjust URL as needed
  const shareText = `Check out this property report: ${propertyId}`;

  // 1. Copy to Clipboard
  try {
    await navigator.clipboard.writeText(propertyId);
  } catch (err) {
    console.error('Failed to copy ID:', err);
  }

  // 2. Try Native Share (Mobile/Chrome), else Fallback
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Property Report',
        text: shareText,
        url: shareUrl,
      });
    } catch (err) {
      console.log('Share cancelled or failed', err);
    }
  } else {
    // Fallback: Open a simple custom prompt or default to WhatsApp
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
    const emailUrl = `mailto:?subject=Property Report&body=${encodeURIComponent(shareText + " " + shareUrl)}`;
    
    Swal.fire({
      title: 'Share Property',
      text: `Property ID ${propertyId} copied to clipboard!`,
      icon: 'success',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: '<i class="fab fa-whatsapp"></i> WhatsApp',
      denyButtonText: '<i class="fas fa-envelope"></i> Email',
      cancelButtonText: 'Close',
      confirmButtonColor: '#25D366',
      denyButtonColor: '#EA4335',
    }).then((result) => {
      if (result.isConfirmed) {
        window.open(whatsappUrl, '_blank');
      } else if (result.isDenied) {
        window.open(emailUrl, '_self');
      }
    });
  }
};



  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex' }}>
        <SideMenu />

        <div className="profile-content-container p-3" id="mainContent" style={{ flex: 1, overflow: 'auto' }}>
          <div className="page-header">
            <div className="page-title-section">
              <h1>Property Measurement Projects</h1>
              <p className="page-subtitle">Manage, track, and analyze all your property measurement projects</p>
            </div>

            <div className="page-actions">
              <BulkUpload />
              <ExportProject selectedIds={selectedProjects} />
              <button className="action-btn" onClick={() => setShowAdvancedModal(true)}>
                <i className="fas fa-filter"></i> Advanced Filter  
              </button>
            </div>
          </div>

          <div className="projects-nav">
            <button className={`nav-tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>All Projects</button>
            <button className={`nav-tab ${activeTab === 'draft' ? 'active' : ''}`} onClick={() => setActiveTab('draft')}>
              Draft <span className="tab-badge">{draftCount}</span>
            </button>
            <button className={`nav-tab ${activeTab === 'queued' ? 'active' : ''}`} onClick={() => setActiveTab('queued')}>
              Queued <span className="tab-badge">{queuedCount}</span>
            </button>
            <button className="nav-tab">Shared with Me</button>
            <CreateFolder />
          </div>

          <div className="filters-bar">
            <div className="search-filter">
              <input 
                type="text" 
                placeholder="Search by Address or Program..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <select className="filter-select" value={reportFilter} onChange={(e) => setReportFilter(e.target.value)}>
              <option value="all">All Report Types</option>
              {distinctReportTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            {/* <div className="date-filter">
              <input type="date" placeholder="dd-mm-yyyy" readOnly style={{cursor: 'default'}} />
            </div> */}

            <div className="items-per-page">
              <span>Show:</span>
              <select className="filter-select">
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
            </div>
          </div>

          <div className="projects-table-container">
            <div className="table-header">
              <div className="table-title">
                <div className="select-all">
                  <input 
                    type="checkbox" 
                    className="custom-checkbox" 
                    onChange={handleSelectAll}
                    checked={selectedProjects.length === filteredProjects.length && filteredProjects.length > 0}
                  />
                  <span id="selectedCount">{selectedProjects.length} Selected</span>
                </div>
              </div>

              <div className="table-actions">
                {/* <button className="table-btn"><i className="fas fa-share-alt"></i> Share</button> */}
                <button className="table-btn" onClick={handleDownloadExcel}>
                  <i className="fas fa-download"></i> Download
                </button>
                {/* <button className="table-btn" onClick={() => handleDelete(selectedProjects)}>
                    <i className="fas fa-trash"></i> Delete
                </button> */}
              </div>
            </div>

            <table className="projects-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}><input type="checkbox" onChange={handleSelectAll} checked={selectedProjects.length === filteredProjects.length && filteredProjects.length > 0} /></th>
                  <th>#</th>
                  <th>Property ID</th>
                  <th>Address</th>
                  <th>Lot Area (sqft / acre)</th>
                  <th>Report Type</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Completed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan="11"><Skeleton height={45} style={{ margin: '5px 0' }} /></td>
                    </tr>
                  ))
                ) : filteredProjects.length === 0 ? (
                  <tr><td colSpan="11" style={{ textAlign: 'center', padding: '20px' }}>No projects found.</td></tr>
                ) : (
                  filteredProjects.map((project, index) => (
                    <tr key={project.id}>
                      <td>
                        <input 
                          type="checkbox" 
                          checked={selectedProjects.includes(project.id)}
                          onChange={() => handleSelectOne(project.id)}
                        />
                      </td>
                      <td>{index + 1}</td>
                      <td> <span className='text-dark pid'>{project.property_id}</span> </td>
                      <td>{project.project_data?.address || '-'}</td>
                      <td className="area-cell text-dark pid">{formatLotArea(project.project_data?.lotArea)}</td>
                      <td>
                        <span className="report-badge-lite">{project.project_data?.reportType}</span>
                      </td>
                      <td style={{minWidth: "140px"}}>
  {project.status.toLowerCase() === 'saved' ? (
    <span 
      className={`status-tag tag-${project.status.toLowerCase()}`}
      style={{ cursor: 'pointer', textDecoration: 'underline' }}
      onClick={() => setStatusModalProject(project)}
    >
      {project.status}
    </span>
  ) : (
    <span className={`status-tag tag-${project.status.toLowerCase()}`}>
      {project.status}
    </span>
  )}
</td>
                      <td style={{minWidth: "100px"}}>
  {new Date(project.created_at).toLocaleDateString('en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC'
  })}
</td>
                      <td>
                        {project.complete_at ? 
                          new Date(project.complete_at).toLocaleDateString('en-GB', {
                            month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC'
                          }) 
                          : '-'
                        }
                      </td>
                      <td className="ai-container">
                        {/* <button className="ai-btn" title="Share"><i className="fas fa-share-alt"></i></button> */}
               

               <button 
    className="ai-btn" 
    title="Copy ID & Share" 
    onClick={() => handleShare(project.property_id)}
  >
    <i className="fas fa-share-alt"></i>
  </button>



{project.status === 'Completed' ? (
    <Link to={`/report/${project.property_id}`} target='_blank' className="ai-btn" title="View">
        <i className="fas fa-eye"></i>
    </Link>
) : (
    <span className="ai-btn disabled" title="Available once completed" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
        <i className="fas fa-eye"></i>
    </span>
)}

{/* Edit Link - Only active if Completed */}
{project.status === 'Completed' ? (

   <span className="ai-btn disabled" title="Available once completed" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
        <i className="fas fa-edit"></i>
    </span>

   
) : (
    <a href={`/?propertyid=${project.property_id}&action=edit`} className="ai-btn" title="Edit">
        <i className="fas fa-edit"></i>
    </a>
)}
                        {/* <button className="ai-btn" title="Delete" onClick={() => handleDelete(project.id)}>
                            <i className="fas fa-trash text-danger"></i>
                        </button> */}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>


            
              



          </div>
        </div>




      </div>



{statusModalProject && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 99999
        }} onClick={() => setStatusModalProject(null)}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '32px',
            width: '400px', boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
          }} onClick={e => e.stopPropagation()}>
            <h5 style={{ marginBottom: '8px', fontWeight: 700 }}>Submit for Processing</h5>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
              Change status of <b>{statusModalProject.project_data?.address || statusModalProject.property_id}</b> from <b>Saved</b> to <b>Queued</b>?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="table-btn" onClick={() => setStatusModalProject(null)}>Cancel</button>
              <button 
                className="table-btn" 
                style={{ background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', color: '#fff' }}
                onClick={handleStatusChange}
                disabled={updatingStatus}
              >
                {updatingStatus ? 'Updating...' : 'Confirm → Pending'}
              </button>
            </div>
          </div>
        </div>
      )}

   

      {/* MODAL CALL WITH FOLDERS PASSED */}
      <AdvancedFilterModal 
        isOpen={showAdvancedModal}
        onClose={() => setShowAdvancedModal(false)}
        currentFilters={advFilters}
        folders={distinctFolders} 
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      <style>
        {`
        .modal-overlay {
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0,0,0,0.5); display: flex; align-items: center;
            justify-content: center; z-index: 9999;
        }
        .status-tag.tag-draft { background-color: #E1EFFF; color: #007BFF; border: 1px solid #B3D7FF; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 600; }
        .status-tag.tag-pending, .status-tag.tag-queued { background-color: #FFF4E5; color: #FF8C00; border: 1px solid #FFE0B3; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 600; }
        .report-badge-lite { background-color: #e8f5e9; color: #2e7d32; padding: 0px 0px; border-radius: 6px; font-size: 12px; font-weight: 600; border: 1px solid #e8f5e9; }
        .area-cell { font-size: 13px; white-space: nowrap; color: #2D3748; }

        .status-tag.tag-saved { background-color: #F0FFF4; color: #276749; border: 1px solid #9AE6B4; padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 600; }


        `}
      </style>


 


    </div>
  );
};

export default ProjectsDashboard;